/**
 * The logo mark's geometry (D-1, ruled 2026-09-24: "A · evolved wordmark"),
 * exactly as the first edition's logo board drew it: two round-capped arcs on
 * one circle of radius 36 in a 100-unit box, stroke 16. Imports nothing, so the
 * home page's mark (Prest0Mark.tsx), the chat skin's rail (pavilionChatCss.ts) and
 * every spinner on the platform — the working mark, the logo's zero turning
 * (src/styles/markSpinnerCss.ts, D-71) — draw from one source without any of them
 * pulling another's chunk.
 */

/** The circle both arcs are written on (the `A36 36` in each path), centred in the box. */
export const MARK_RADIUS = 36;
/** The ink arc: the larger crescent, open at the lower right. */
export const MARK_INK_ARC = 'M40.7 84.8A36 36 0 1 1 82.6 34.8';
/** The gold arc: the lower-right crescent. */
export const MARK_GOLD_ARC = 'M85.5 43.7A36 36 0 0 1 50 86';
export const MARK_STROKE = 16;
/** The gold arc's own height, stroke included (y 35.7 to 94): where its satin gradient runs. */
export const MARK_GOLD_Y1 = 35;
export const MARK_GOLD_Y2 = 94;

/** The satin gold's token names, lit from above, and where each sits on the arc (the coin's face uses the same order). */
export type SatinTokens = Record<'gold-sky' | 'gold-hi' | 'accent' | 'gold-mid' | 'gold-lo', string>;
export function satinStops(t: SatinTokens): Array<[string, string]> {
  return [
    ['0', t['gold-sky']],
    ['.3', t['gold-hi']],
    ['.56', t.accent],
    ['.8', t['gold-mid']],
    ['1', t['gold-lo']],
  ];
}

/**
 * The mark as a standalone SVG document (for a data URI, where there is no page to inherit an ink from): the ink arc
 * in `ink`, the gold arc in the satin stops given. The stops stay attributes, as in the inline mark.
 */
export function markSvgDocument(ink: string, gold: SatinTokens): string {
  const stops = satinStops(gold)
    .map(([offset, color]) => `<stop offset="${offset}" stop-color="${color}"/>`)
    .join('');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">` +
    `<defs><linearGradient id="g" gradientUnits="userSpaceOnUse" x1="0" y1="${MARK_GOLD_Y1}" x2="0" y2="${MARK_GOLD_Y2}">${stops}</linearGradient></defs>` +
    `<path d="${MARK_INK_ARC}" fill="none" stroke="${ink}" stroke-width="${MARK_STROKE}" stroke-linecap="round"/>` +
    `<path d="${MARK_GOLD_ARC}" fill="none" stroke="url(#g)" stroke-width="${MARK_STROKE}" stroke-linecap="round"/>` +
    `</svg>`
  );
}
